import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  getBookingApprovalApiV1BookingApprovalsApprovalIdGet,
  listBookingApprovalsApiV1BookingApprovalsGet,
} from "../../api/generated/sdk.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";

export type BookingApprovalsFilters = {
  bookingId?: number;
  medicalExaminer?: string;
  referrer?: string;
  examinee?: string;
  appointmentDate?: string;
  secureAndSwitch?: boolean;
  matchStatus?: string;
  hasDocuments?: boolean;
};

/**
 * List booking approvals (admin), optionally filtered by status + the
 * advanced filter bar fields, paginated.
 * @param status - PENDING / APPROVED / REJECTED (omit for all)
 * @param page - 1-indexed page number (default 1)
 * @param limit - page size (default 20)
 */
export const useBookingApprovals = ({
  status,
  filters = {},
  page = 1,
  limit = 20,
  enabled = true,
}: {
  status?: string;
  filters?: BookingApprovalsFilters;
  page?: number;
  limit?: number;
  enabled?: boolean;
} = {}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.BOOKING_APPROVALS,
      { status: status ?? null, filters, page, limit },
    ],
    queryFn: async () => {
      const response = await listBookingApprovalsApiV1BookingApprovalsGet({
        client: apiClient,
        query: {
          status,
          page,
          limit,
          booking_id: filters.bookingId,
          medical_examiner: filters.medicalExaminer || undefined,
          referrer: filters.referrer || undefined,
          examinee: filters.examinee || undefined,
          appointment_date: filters.appointmentDate || undefined,
          secure_and_switch: filters.secureAndSwitch,
          match_status: filters.matchStatus || undefined,
          has_documents: filters.hasDocuments,
        },
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch booking approvals: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    // Keep showing the prior page/results while a filter change or page
    // navigation's request is in flight, instead of flashing to the loading
    // state on every keystroke/page change.
    placeholderData: keepPreviousData,
    enabled,
  });
};

/** Fetch a single booking approval by id (admin). */
export const useBookingApproval = ({
  id,
  enabled = true,
}: {
  id: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.BOOKING_APPROVALS, id],
    queryFn: async () => {
      const response = await getBookingApprovalApiV1BookingApprovalsApprovalIdGet({
        client: apiClient,
        path: { approval_id: id },
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch booking approval: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    enabled: enabled && !!id,
  });
};
